import React, { useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import Loader from "./loader";

export default function AppModal({
    isOpen,
    onClose,
    icon,
    title,
    body,
    primary,
    secondary,
    showClose = true,
    className = "",
    footer,
    disableOutsideClose = false,
    iconClass,
}) {
    const panelRef = useRef(null);
    const isBusy = useMemo(
        () => Boolean(primary?.loading || secondary?.loading),
        [primary, secondary]
    );

    // Close on ESC
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            if (disableOutsideClose || isBusy) return;
            onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose, disableOutsideClose, isBusy]);

    if (!isOpen) return null;

    const btnStyles = {
        primary: "bg-[#6F00FF] hover:bg-[#5A00D6] text-white",
        danger: "bg-[#F04438] hover:bg-[#D92D20] text-white",
        neutral: "bg-[#EEF0F6] hover:bg-[#E5E7F0] text-[#101828]",
    };

    const handleBackdropClick = (e) => {
        if (disableOutsideClose || isBusy) return;
        if (panelRef.current && !panelRef.current.contains(e.target)) onClose?.();
    };

    return (
        <div
            className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-[1px] flex items-center justify-center p-4"
            onMouseDown={handleBackdropClick}
            role="dialog"
            aria-modal="true"
        >
            <div
                ref={panelRef}
                className={`relative w-full max-w-[680px] bg-white rounded-2xl shadow-xl !py-5 ${className}`}
            >
                {/* Close */}
                {showClose && !isBusy && (
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute right-4 top-4 text-[#101828]/70 hover:text-[#101828]"
                    >
                        <X className="h-6 w-6" />
                    </button>
                )}

                {/* Icon */}
                {icon && (
                    <div className="flex justify-center mb-4 sm:mb-6">
                        <div
                            className={`h-10 w-10 sm:h-20 sm:w-20 grid place-items-center rounded-full ${iconClass}`}
                        >
                            {icon}
                        </div>
                    </div>
                )}

                {/* Title */}
                <h2 className="text-center !px-5 text-[24px] font-[600] text-[#161827]">{title}</h2>

                {/* Body */}
                <div className="mt-3 !px-5 mb-8 sm:mt-5 text-center text-[#383B56] text-[16px]">
                    {typeof body === "string" ? <p>{body}</p> : body}
                </div>

                <div className="border my-1" />

                {/* Footer */}
                <div className="mt-3 !px-5">
                    {footer ? (
                        footer
                    ) : (
                        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 justify-center sm:justify-end">
                            {secondary && (
                                <button
                                    onClick={secondary.onClick}
                                    className="!px-5 !py-3 rounded-lg text-[#344054] bg-transparent hover:bg-[#F2F3FC]"
                                    disabled={secondary.disabled || isBusy || secondary.loading}
                                >
                                    {secondary.loading ? <Loader /> : secondary.label}
                                </button>
                            )}
                            {primary && (
                                <button
                                    onClick={primary.onClick}
                                    className={`!px-5 !py-3 rounded-lg font-medium ${
                                        btnStyles[primary.variant || "primary"]
                                    }`}
                                    disabled={primary.disabled || isBusy || primary.loading}
                                >
                                    {primary.loading ? <Loader /> : primary.label}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
